import { useEffect, useState } from "react";
import { useWorkspaceAccounts } from "@api/account.api";
import {
    getMigratedScheduleStartError,
    resolveMigratedScheduleHoldingAccountId,
} from "@common/helpers/domains/accounting/migratedSchedule";
import { isScheduleDeferringMode } from "@common/helpers/domains/accounting/scheduleDeferringMode";
import { ScheduleTemplateResponse } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import {
    RevenueSubMode,
    ScheduleType,
} from "@common/types/domains/accounting/schedule";
import { Entity } from "@common/types/entity";
import {
    enterMigratedScheduleDraft,
    leaveMigratedScheduleDraft,
    MigratedSchedulePrevious,
} from "./migratedScheduleDraft";
import { moveScheduleDraftTermStart } from "./scheduleDraftRows";
import { ScheduleDraftState } from "./useScheduleDraftState";

interface MigratedScheduleState {
    templateId: string;
    entityId: number;
    previous: MigratedSchedulePrevious;
}

export interface MigratedScheduleOption {
    isChecked: boolean;
    /** The earliest start a migrated schedule can take: the bookkeeping start. */
    minDate: string | undefined;
    onCheckedChange: (isChecked: boolean) => void;
}

export interface MigratedScheduleController {
    /** Null unless the policy defers: only a deferred balance can come in with the opening balances. */
    option: MigratedScheduleOption | null;
    isMigrated: boolean;
    /** Why the start can't be used, shown under the rows. */
    startError: string | null;
}

/**
 * The manual apply's "Migrated schedule" choice. Checking it moves the draft
 * to Kick's Deferred Revenue account (when the chart has it) and to the
 * entity's bookkeeping start; unchecking puts both back. A different entity
 * or policy reads as unchecked, so the choice never outlives what it was
 * made for.
 */
export function useMigratedScheduleOption(options: {
    template: ScheduleTemplateResponse | undefined;
    entity: Entity | undefined;
    draftState: ScheduleDraftState;
    termStart: string;
}): MigratedScheduleController {
    const { template, entity, draftState, termStart } = options;
    const [state, setState] = useState<MigratedScheduleState | null>(null);
    const workspaceAccounts = useWorkspaceAccounts();
    const { draft, setDraft } = draftState;

    const isAvailable =
        template !== undefined &&
        entity !== undefined &&
        isScheduleDeferringMode(template);
    const isMigrated =
        isAvailable &&
        state !== null &&
        state.templateId === template.id &&
        state.entityId === entity.id;

    useEffect(() => {
        if (state === null || isMigrated) {
            return;
        }
        setState(null);
        if (draft && state.templateId === template?.id) {
            setDraft(
                moveScheduleDraftTermStart({
                    draft,
                    termStart: state.previous.termStart,
                }),
            );
        }
    }, [state, isMigrated, draft, setDraft, template?.id]);

    const entityAccounts = (workspaceAccounts.data ?? []).filter(
        (account) => account.entityId === entity?.id,
    );

    const onCheckedChange = (isChecked: boolean) => {
        if (!isAvailable || !draft) {
            return;
        }
        if (isChecked) {
            const holdingAccountId =
                template.type === ScheduleType.revenue
                    ? resolveMigratedScheduleHoldingAccountId({
                          revenueSubMode:
                              template.revenueSubMode ??
                              RevenueSubMode.deferred,
                          accounts: entityAccounts,
                      })
                    : null;
            setState({
                templateId: template.id,
                entityId: entity.id,
                previous: {
                    holdingAccountId: draft.holdingAccountId,
                    termStart: draft.termStart,
                },
            });
            setDraft(
                enterMigratedScheduleDraft({
                    draft,
                    holdingAccountId,
                    bookkeepingStartDate: entity.bookkeepingStartDate,
                }),
            );
            return;
        }
        if (state !== null) {
            setDraft(
                leaveMigratedScheduleDraft({
                    draft,
                    previous: state.previous,
                }),
            );
        }
        setState(null);
    };

    return {
        option: isAvailable
            ? {
                  isChecked: isMigrated,
                  minDate: isMigrated ? entity.bookkeepingStartDate : undefined,
                  onCheckedChange,
              }
            : null,
        isMigrated,
        startError:
            isMigrated && entity
                ? getMigratedScheduleStartError({
                      termStart,
                      bookkeepingStartDate: entity.bookkeepingStartDate,
                  })
                : null,
    };
}
